// @ts-nocheck
import {
    Text,
    Button, Flex, Title, Space, Loader, useMantineTheme, PasswordInput
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "@mantine/form";
import { completeActivation } from "@/PageComponents/Login/LoginRequests";
import Link from "next/link";
import { showNotification, updateNotification } from "@mantine/notifications";
import Helper from "@/utils/helper";
import PasswordRequirements from "@/components/sc-controls/password-requirements";
import { IconArrowRight, IconCheck } from "@tabler/icons";

const SetPasswordForm = ({ token }: any) => {
    const theme = useMantineTheme();

    const form = useForm({
        initialValues: {
            password1: '',
            password2: '',
            visible: false,
        },
        validate: {
            password1: (password1: any, { password2 }: any) => {
                if (/\s/.test(password1)) return 'Password cannot contain spaces';

                const strengthError = Helper.getPasswordStrengthError(password1, {});
                if (strengthError) return strengthError;

                if ((password1 || '').length > 100) return 'Please use a password of 100 characters or less';

                return password1 !== password2 ? ' ' : null;
            },
            password2: (password2: any, { password1 }: any) => {
                if (/\s/.test(password2)) return 'Password cannot contain spaces';
                return password1 !== password2 ? 'Passwords do not match' : null;
            }
        }
    });

    const activateMutation = useMutation(['activation', 'complete'], completeActivation,
        {
            onError: (error: any) => {
                try { form.setFieldError('password1', error?.message || 'Something went wrong'); } catch { }
                updateNotification({
                    id: 'activateAccount',
                    color: 'yellow.7',
                    message: error.message || 'Failed to activate account',
                    loading: false
                });
                console.error(error);
            },
            onSuccess: () => {
                updateNotification({
                    id: 'activateAccount',
                    color: 'green',
                    message: 'Your account has been activated successfully!',
                    loading: false
                });
            }
        }
    );

    const handleSubmit = ({ password1 }: any) => {
        if (form.isValid) {
            showNotification({
                id: 'activateAccount',
                color: 'scBlue',
                loading: true,
                message: 'Activating your account...'
            });
            activateMutation.mutate({ password: password1, token });
        }
    };

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            <Title size="h2" fw="bolder">
                {activateMutation.isSuccess ? 'Account Activated!' : 'Activate Your Account'}
            </Title>

            <Text className="subheading" size="sm" c="dimmed">
                {activateMutation.isSuccess
                    ? 'Your account is ready to use'
                    : 'Set your password to complete account activation'}
            </Text>

            <Space my="var(--mantine-spacing-md)" />

            {!activateMutation.isSuccess ? (
                <>
                    <PasswordInput
                        mb="var(--mantine-spacing-lg)"
                        placeholder="Enter your password"
                        label="Password"
                        visible={form.values.visible}
                        onVisibilityChange={(v: any) => form.setFieldValue('visible', v)}
                        minLength={10}
                        maxLength={100}
                        required
                        {...form.getInputProps('password1')}
                    />

                    <PasswordInput
                        mb={0}
                        placeholder="Confirm your password"
                        label="Confirm Password"
                        visible={form.values.visible}
                        onVisibilityChange={(v: any) => form.setFieldValue('visible', v)}
                        minLength={10}
                        maxLength={100}
                        required
                        {...form.getInputProps('password2')}
                    />

                    <PasswordRequirements password={form.values.password1} confirmPassword={form.values.password2} />
                </>
            ) : (
                <Flex direction="column" align="center" py="lg">
                    <IconCheck size={48} color={theme.colors.green[6]} />
                    <Text mt="md" ta="center">Your account has been activated successfully!</Text>
                    <Text c="dimmed" mt="sm" ta="center">You can now log in with your new password.</Text>
                </Flex>
            )}

            <Flex mt="xl" justify={activateMutation.isSuccess ? "center" : "space-between"}>
                {!activateMutation.isSuccess && (
                    <Link href="/login">
                        <Button type="button" mt="lg" color="scBlue" variant="outline">
                            Back to Login
                        </Button>
                    </Link>
                )}
                {activateMutation.isSuccess ? (
                    <Link href="/login">
                        <Button mt="lg" color="scBlue" size="md">
                            Go to Login
                        </Button>
                    </Link>
                ) : (
                    <Button
                        type="submit"
                        mt="lg"
                        color="scBlue"
                        rightSection={activateMutation.isLoading ? (
                            <Loader variant="oval" size={18} color="white" />
                        ) : (
                            <IconArrowRight />
                        )}
                        disabled={activateMutation.isLoading || !form.isValid}
                    >
                        Activate Account
                    </Button>
                )}
            </Flex>
        </form>
    );
};

export default SetPasswordForm;
